import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function SpecialOccasionsCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type}s for Special Occasions`}
        type='website'
        canonical='/food-truck-catering/special-occasions'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Book food ${vehicleType}s for special occasions in ${city_name}. Grand openings, engagement parties, product launches, charity events, trade shows & more.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Special Occasion Catering in ${city_name}`,
            description: `Book food ${vehicleType}s for special occasions in ${city_name}. Grand openings, engagement parties, product launches, charity events and more.`,
            url: '/food-truck-catering/special-occasions',
            serviceType: 'Food Truck Special Occasion Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Special Occasions', item: '/food-truck-catering/special-occasions' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Food {vehicle_type} Catering for Special Occasions</h1>
            <p>Some occasions call for something more memorable than standard catering. Whether you&#39;re hosting a grand opening, celebrating an engagement, launching a product, or organizing a charity event, a food {vehicleType} brings energy, great food, and a unique experience that guests won&#39;t forget. {site_title} connects you with {city_name}&#39;s best food {vehicleType}s so you can make any special occasion truly stand out.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Get Free Catering Quotes</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Popular Food ${vehicle_type}s for Special Occasions`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Food ${vehicle_type} for a Special Occasion?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Perfect for Any Special Occasion</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Grand Openings & Product Launches</h3>
            <p>Draw a crowd and create buzz with a food {vehicleType} at your grand opening or product launch. Nothing attracts foot traffic like the sight and smell of fresh, made-to-order food. Whether you&#39;re opening a new retail location, launching a product, or hosting a real estate open house, a food {vehicleType} makes your event feel like a celebration.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Engagement Parties & Personal Milestones</h3>
            <p>Celebrate your engagement, retirement, or other personal milestone with a food {vehicleType} that matches the vibe. From elegant appetizer {vehicleType}s to fun taco and dessert bars, {city_name}&#39;s food {vehicleType}s offer a laid-back yet impressive catering option that lets you focus on the celebration, not the logistics.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Charity Events, Trade Shows & Conferences</h3>
            <p>Food {vehicleType}s are a practical and crowd-pleasing solution for charity events, trade shows, conferences, and team-building outings. They handle all the food prep and service, keep lines moving efficiently, and offer diverse menu options to accommodate large groups. Many {vehicleType}s in {city_name} have experience with high-volume events and can customize their setup to fit your venue and schedule.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Book a Food ${vehicle_type} for Your Special Occasion!`} />
      </section>
    </div>
  );
}

export default SpecialOccasionsCatering;
